// Promos del teléfono en «Arma la venta» (slot `salePromotion`): una que va sola (o «Ninguna») y
// las acumulables que se suman, en las cajas de color del catálogo. «Crédito» va aparte, solo lectura.
import { useEffect, useRef, useState } from 'react';
import { Gift, Sparkles, type LucideIcon } from 'lucide-react';
import { CreditPromotionsGroup } from '@/domains/sales/wizard/components/CreditPromotionsGroup';
import { NoneChoiceRow, PromotionGroup } from '@/domains/sales/wizard/components/PromotionGroup';
import type {
    AnchorSlotContext,
    SetSelectionPending,
} from '@/domains/sales/wizard/steps/BuildSaleStep';
import { Button } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import { useSalePromotionEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import type {
    EvaluatedSalePromotion,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import { anchorKeyOf, useReapplyOnSaleChange } from '../hooks/useReapplyOnSaleChange';
import { chargedLockCopy, promotionErrorCopy } from '../lib/promotionErrorCopy';
import { forAnchor } from '../lib/promotionAppliesTo';
import { giftValueCentsOf } from '../lib/giftPanel';
import { splitByBenefit, type BenefitGroup } from '../lib/skuPromotionPieces';
import { useAnchorSaleContext } from '../hooks/useAnchorSaleContext';
import { afterSaleText } from './AfterSaleBenefits';
import {
    EMPTY_LINE,
    normalizeKinds,
    withSolo,
    withStackable,
    type PromotionChoice,
} from '../lib/promotionSelection';
import type { SelectionChange } from '../lib/applySelection';
import {
    usePreviewSelection,
    usePromotionSelectionStore,
    useSessionPromotionState,
} from '../store/promotionSelectionStore';
import { rememberedPromotionName } from '../lib/promotionNames';
import { PROMOTIONS_CONFIRMING_REASON, PROMOTIONS_RETRY_REASON } from '../constants';
import { ChoiceRow } from './ChoiceRow';

export { lineBreakdown } from './PromotionChoices';
import { GROUPS, SectionTitle, SoloPromotionRow, StackableChoice } from './PromotionChoices';

export interface SalePromotionPickerProps extends AnchorSlotContext {
    sessionId: string;
    /** Bloquea «Continuar» mientras la promo no esté confirmada; `reason` sale bajo el botón. */
    setSelectionPending?: SetSelectionPending;
}

/** Nombres de los regalos que trae la promo de venta elegida. */
function includedGiftNames(
    promos: readonly EvaluatedSalePromotion[],
    sale: PromotionChoice | null,
): string[] {
    if (!sale) return [];
    const promo = promos.find((p) => p.promotionId === sale.promotionId);
    if (!promo) return [];
    return sale.giftSkus.map(
        (sku: string) => promo.giftOptions.find((o) => o.sku === sku)?.name ?? sku,
    );
}

/** El alcance llega al teléfono; lo que no se sabe del equipo (gama, categoría) no lo descarta. */
function scopeReachesPhone(
    promo: Pick<StoreSalePromotionItem, 'scope'>,
    anchor: AnchorSlotContext['anchorLine'],
): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    const category =
        anchor.product?.category ?? (anchor.components.length > 0 ? 'BUNDLE' : 'PHONE');
    const tier = anchor.product?.tier;
    return (
        (skus === null || skus.includes(anchor.sku)) &&
        (productCategories === null || productCategories.includes(category)) &&
        (tiers === null || !tier || tiers.includes(tier as (typeof tiers)[number]))
    );
}

/** Promos del teléfono (las de toda la venta cuentan como suyas), filtradas por SureKeep. */
export function usePhonePromotions(
    sessionId: string,
    withoutSureKeep: boolean,
    anchorLine: AnchorSlotContext['anchorLine'],
) {
    const evaluation = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    // Una con alcance que no llega al teléfono (p. ej. solo accesorios) va en la tarjeta del accesorio.
    const scopes = new Map((store?.items ?? []).map((p) => [p.promotionId, p]));
    const promos = forAnchor(
        (evaluation.data?.promotions ?? []).filter((p) => {
            if (!p.anchorBound) return false;
            const scoped = scopes.get(p.promotionId);
            return !scoped || scopeReachesPhone(scoped, anchorLine);
        }),
        withoutSureKeep,
    );
    const solos = promos.filter((p) => !p.stackable);
    const stackables = promos.filter((p) => p.stackable);
    return {
        evaluation,
        promos,
        solos: splitByBenefit(solos, afterSaleText),
        stackables: splitByBenefit(stackables, afterSaleText),
        byId: new Map(promos.map((p) => [p.promotionId, p])),
    };
}

/** Controlador único del teléfono: un apartado, un error y un regalo pendiente. */
function usePhoneChoice(
    sessionId: string,
    context: AnchorSlotContext,
    setSelectionPending: SetSelectionPending,
    phone: ReturnType<typeof usePhonePromotions>,
) {
    const { saleDeclined } = useSessionPromotionState(sessionId);
    const selection = usePreviewSelection(sessionId);
    const patch = usePromotionSelectionStore((s) => s.patch);
    const { apply, isApplying, error, canWrite } = useApplyPromotions({ sessionId, context });
    const reapply = useReapplyOnSaleChange({ sessionId, context });
    const { evaluation } = phone;
    const anchorLineId = context.anchorLine.lineId;
    const view = evaluation.data ? normalizeKinds(selection, evaluation.data) : selection;
    const line = view.byLine[anchorLineId] ?? EMPTY_LINE;
    // Promo que va sola con varios regalos, a la espera de que el cliente escoja uno.
    const [picking, setPickingGiftFor] = useState<{
        promo: EvaluatedSalePromotion;
        anchorKey: string;
    }>();
    const anchorKey = anchorKeyOf(context);
    // Si la promo pendiente ya no está (cambió el equipo, se pausó o se bloqueó), deja de esperar.
    const pendingPromo = picking ? phone.byId.get(picking.promo.promotionId) : undefined;
    const pickingGiftFor =
        picking && picking.anchorKey === anchorKey && pendingPromo && !pendingPromo.blockedBy
            ? picking.promo
            : undefined;
    const writing = isApplying || reapply.isReapplying;
    // «Continuar» espera a que la promo quede confirmada; el motivo sale bajo el botón.
    const reason =
        evaluation.isError || reapply.error
            ? PROMOTIONS_RETRY_REASON
            : evaluation.isLoading || writing
              ? PROMOTIONS_CONFIRMING_REASON
              : pickingGiftFor
                ? 'Elige el regalo de la promo.'
                : undefined;
    useEffect(() => {
        setSelectionPending(reason !== undefined, reason);
    }, [reason, setSelectionPending]);
    const charged = context.quoteState === 'charged';
    // Los cambios se marcan al instante y van en fila; solo una venta cobrada bloquea.
    const busy = charged;
    const soloId = pickingGiftFor ? null : (line.solo?.promotionId ?? null);
    const run = (change: SelectionChange): void => {
        context
            .confirmQuoteDiscard()
            .then((ok) => (ok ? apply(change) : undefined))
            .catch(() => undefined);
    };
    const setSolo = (choice: PromotionChoice | null): SelectionChange => {
        return (base) => {
            const normalized = evaluation.data ? normalizeKinds(base, evaluation.data) : base;
            const anchor = normalized.anchorLineId ?? anchorLineId;
            return withSolo({ ...normalized, anchorLineId: anchor }, anchor, choice);
        };
    };
    const chooseSolo = (promo: EvaluatedSalePromotion): void => {
        const inStock = promo.giftOptions.filter((o) => o.inStock);
        if (inStock.length > 1) {
            setPickingGiftFor({ promo, anchorKey });
            return;
        }
        setPickingGiftFor(undefined);
        run(
            setSolo(
                choiceOf(
                    promo,
                    inStock.map((o) => o.sku),
                ),
            ),
        );
    };
    const pickGift = (promo: EvaluatedSalePromotion, sku: string): void => {
        setPickingGiftFor(undefined);
        run(setSolo(choiceOf(promo, [sku])));
    };
    const toggleStackable = (promo: EvaluatedSalePromotion, giftSkus: string[], on: boolean) => {
        run((base) => {
            const anchor = base.anchorLineId ?? anchorLineId;
            return withStackable(
                { ...base, anchorLineId: anchor },
                anchor,
                choiceOf(promo, giftSkus),
                on,
            );
        });
    };
    const declineSolo = (): void => {
        setPickingGiftFor(undefined);
        context
            .confirmQuoteDiscard()
            .then((ok) => {
                if (!ok) return undefined;
                patch(sessionId, { saleDeclined: true });
                return apply(setSolo(null));
            })
            .catch(() => undefined);
    };
    // Sin «va sola» elegida (y sin «Ninguna»), se preselecciona la primera que aplica; una vez por ancla.
    const first = GROUPS.flatMap(({ group }) => phone.solos[group])
        .map((piece) => phone.byId.get(piece.promotionId))
        .find((promo): promo is EvaluatedSalePromotion => !!promo && preselectable(promo));
    const autoKey = first ? `${anchorKey}|${first.promotionId}` : undefined;
    const autoTried = useRef<string | undefined>(undefined);
    const idle = !isApplying && !reapply.isReapplying && !evaluation.isLoading;
    const noSolo = line.solo === null;
    useEffect(() => {
        if (!first || !autoKey || autoTried.current === autoKey) return;
        if (context.quoteState !== 'open' || !canWrite || saleDeclined || !idle) return;
        if (!noSolo || pickingGiftFor) return;
        autoTried.current = autoKey;
        apply(
            setSolo(
                choiceOf(
                    first,
                    first.giftOptions.filter((o) => o.inStock).map((o) => o.sku),
                ),
            ),
        ).catch(() => undefined);
    });
    return {
        line,
        error,
        reapply,
        charged,
        busy,
        soloId,
        chooseSolo,
        pickGift,
        toggleStackable,
        declineSolo,
        pickingGiftFor,
    };
}

function choiceOf(promo: EvaluatedSalePromotion, giftSkus: string[]): PromotionChoice {
    return { promotionId: promo.promotionId, promotionVersion: promo.version, giftSkus };
}

/** Se puede preseleccionar: no bloqueada y sin regalo que haya que escoger (o sin existencia). */
function preselectable(promo: EvaluatedSalePromotion): boolean {
    if (promo.blockedBy) return false;
    const inStock = promo.giftOptions.filter((o) => o.inStock).length;
    return promo.giftOptions.length === 0 || inStock === 1;
}

export function SalePromotionPicker({
    sessionId,
    setSelectionPending = noPending,
    ...context
}: SalePromotionPickerProps) {
    const { reapplyError } = useSessionPromotionState(sessionId);
    const anchorSale = useAnchorSaleContext(sessionId, context.anchorLine);
    const phone = usePhonePromotions(sessionId, anchorSale.withoutSureKeep, context.anchorLine);
    const choice = usePhoneChoice(sessionId, context, setSelectionPending, phone);
    const { promos, evaluation } = phone;
    const message =
        choice.error ??
        choice.reapply.error ??
        (reapplyError ? promotionErrorCopy(reapplyError) : null);
    const pickingGiftFor = choice.pickingGiftFor;
    const held = context.salePromotion;
    const hasSolos = GROUPS.some(({ group }) => phone.solos[group].length > 0);
    const hasStackables = GROUPS.some(({ group }) => phone.stackables[group].length > 0);
    const showSale = promos.length > 0 || message !== null || evaluation.isError;
    const saleGiftNames = pickingGiftFor ? [] : includedGiftNames(promos, choice.line.solo);

    const giftChooser = pickingGiftFor && (
        <div
            role="radiogroup"
            aria-label="Regalo de la promo"
            className="ml-6 flex flex-col gap-1.5"
        >
            <p className="text-[12px] text-muted-foreground">Elige el regalo de la promo:</p>
            {pickingGiftFor.giftOptions.map((option) => (
                <ChoiceRow
                    key={option.sku}
                    checked={false}
                    disabled={!option.inStock || choice.busy}
                    onSelect={() => {
                        choice.pickGift(pickingGiftFor, option.sku);
                    }}
                    title={option.name}
                    detail={option.inStock ? undefined : 'Sin existencia'}
                />
            ))}
        </div>
    );

    /** Renglones acumulables de un grupo; un regalo con opciones da un renglón por opción. */
    const stackableItems = (group: BenefitGroup, icon: LucideIcon) =>
        phone.stackables[group].flatMap((piece) => {
            const promo = phone.byId.get(piece.promotionId);
            if (!promo) return [];
            const held = choice.line.stackables.find((c) => c.promotionId === promo.promotionId);
            const disabled = Boolean(promo.blockedBy) || choice.busy;
            const options = promo.kind === 'GIFT' ? promo.giftOptions : [];
            if (options.length === 0) {
                return [
                    {
                        key: piece.promotionId,
                        node: (
                            <StackableChoice
                                piece={piece}
                                icon={icon}
                                checked={held !== undefined}
                                disabled={disabled}
                                description={[...piece.texts, piece.extras]
                                    .filter(Boolean)
                                    .join(' · ')}
                                onToggle={() => {
                                    choice.toggleStackable(
                                        promo,
                                        promo.giftOptions
                                            .filter((o) => o.inStock)
                                            .map((o) => o.sku),
                                        held === undefined,
                                    );
                                }}
                            />
                        ),
                    },
                ];
            }
            return options.map((option) => {
                const checked = held?.giftSkus.includes(option.sku) ?? false;
                return {
                    key: `${piece.promotionId}|${option.sku}`,
                    node: (
                        <StackableChoice
                            piece={piece}
                            icon={icon}
                            label={option.name}
                            checked={checked}
                            disabled={disabled || !option.inStock}
                            description={
                                option.inStock
                                    ? `Valor ${formatMXN(giftValueCentsOf(option) / 100)} · a $0`
                                    : 'Sin existencia'
                            }
                            onToggle={() => {
                                choice.toggleStackable(promo, [option.sku], !checked);
                            }}
                        />
                    ),
                };
            });
        });

    return (
        <>
            {showSale && (
                <section className="flex flex-col gap-2" aria-label="Promos de venta">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                        <Sparkles className="size-3.5" aria-hidden />
                        Promos de venta
                    </div>
                    {evaluation.isError && (
                        <div role="alert" className="flex flex-col gap-1 text-[12px]">
                            <span className="text-destructive">
                                No pudimos cargar las promociones.
                            </span>
                            {held && (
                                <span className="text-muted-foreground">
                                    Apartada:{' '}
                                    {rememberedPromotionName(sessionId, held.promotionId) ??
                                        'promo de venta'}
                                </span>
                            )}
                            <Button
                                size="sm"
                                variant="secondary"
                                className="self-start"
                                isLoading={evaluation.isFetching}
                                onClick={() => void evaluation.refetch()}
                            >
                                Reintentar
                            </Button>
                        </div>
                    )}
                    {hasSolos && (
                        <>
                            <SectionTitle title="Va sola" hint="elige una" />
                            <div
                                role="radiogroup"
                                aria-label="Promos de venta"
                                className="flex flex-col"
                            >
                                {GROUPS.map(({ group, label, icon }) => (
                                    <PromotionGroup
                                        key={group}
                                        label={label}
                                        icon={icon}
                                        tone={group}
                                        limit={Number.POSITIVE_INFINITY}
                                        items={phone.solos[group].flatMap((piece) => {
                                            const promo = phone.byId.get(piece.promotionId);
                                            if (!promo) return [];
                                            const pending =
                                                pickingGiftFor?.promotionId === promo.promotionId;
                                            return [
                                                {
                                                    key: piece.promotionId,
                                                    node: (
                                                        <>
                                                            <SoloPromotionRow
                                                                piece={piece}
                                                                promo={promo}
                                                                icon={icon}
                                                                checked={
                                                                    choice.soloId ===
                                                                    promo.promotionId
                                                                }
                                                                pending={pending}
                                                                disabled={
                                                                    Boolean(promo.blockedBy) ||
                                                                    choice.busy
                                                                }
                                                                names={anchorSale.lineNames}
                                                                onSelect={() => {
                                                                    choice.chooseSolo(promo);
                                                                }}
                                                            />
                                                            {/* El selector del regalo va justo debajo de su fila. */}
                                                            {pending && giftChooser}
                                                        </>
                                                    ),
                                                },
                                            ];
                                        })}
                                    />
                                ))}
                                <div className="mt-3">
                                    <NoneChoiceRow
                                        label="Ninguna"
                                        checked={
                                            choice.soloId === null && pickingGiftFor === undefined
                                        }
                                        disabled={choice.busy}
                                        onSelect={choice.declineSolo}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {hasStackables && (
                        <>
                            <SectionTitle title="Se suman" hint="marca las que quieras" />
                            <div role="group" aria-label="Se suman" className="flex flex-col">
                                {GROUPS.map(({ group, label, icon }) => (
                                    <PromotionGroup
                                        key={group}
                                        label={label}
                                        icon={icon}
                                        tone={group}
                                        limit={Number.POSITIVE_INFINITY}
                                        items={stackableItems(group, icon)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    {saleGiftNames.length > 0 && (
                        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <Gift className="size-3.5 flex-none" aria-hidden />
                            <span>
                                Incluye de regalo:{' '}
                                <b className="font-medium text-foreground">
                                    {saleGiftNames.join(', ')}
                                </b>
                            </span>
                        </p>
                    )}
                    {choice.charged && (
                        <p role="status" className="text-[12px] text-muted-foreground">
                            {chargedLockCopy('sale')}
                        </p>
                    )}
                    {message && (
                        <div role="alert" className="flex flex-col gap-1 text-[12px]">
                            <span className="text-destructive">{message}</span>
                            {choice.reapply.error && (
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    className="self-start"
                                    isLoading={choice.reapply.isReapplying}
                                    onClick={choice.reapply.retry}
                                >
                                    Reintentar
                                </Button>
                            )}
                        </div>
                    )}
                </section>
            )}
            {/* Crédito va aparte: no es una promo de venta y se aplica al cotizar. */}
            <CreditPromotionsGroup
                rows={anchorSale.creditRows}
                clientLevel={undefined}
                note={anchorSale.creditNote}
                applied
                appliedLevel={anchorSale.creditLevel}
            />
        </>
    );
}

const noPending: SetSelectionPending = () => undefined;
